// A stored markdown body drawn statically, with the editor's own parser and
// classes (markdown-body.tsx draws it until the live copy has synced; chats
// and notes draw it on its own). Apart from the live editor so that what only
// reads a body (a chat's replies) does not load the editor's code.
import { DOCUMENT_BODY_CLASS, renderMarkdownToHtml } from "@trameria/editor";
import { useMemo, useRef } from "react";
import { StaticRefChips } from "../refs";
import { MediaLayers } from "./blocks/media";
import { type StaticBodyDress, StaticBodyHtml } from "./blocks/static-body";

/**
 * The body's look, for the static view and the editor alike. The editor's
 * minimum height is taken by the static view too, so the area never collapses
 * while it switches. A document's text is the strongest text color (the old
 * web's), not the softer body color of lists and chats.
 */
export const BODY_CLASS = `${DOCUMENT_BODY_CLASS} text-fg select-text [&_.tiptap]:min-h-[50vh]`;

function htmlOf(markdown: string, lineBreaks: boolean): string | null {
  try {
    return renderMarkdownToHtml(markdown, undefined, "document", {
      lineBreaks,
    });
  } catch {
    return null;
  }
}

/**
 * The stored markdown, drawn as the editor would draw it (its parts dressed
 * the same: blocks/static-body.tsx). It hosts the image and video viewers
 * while it shows.
 */
export function StaticBody({
  markdown,
  className = BODY_CLASS,
  dress,
  lineBreaks = false,
}: {
  markdown: string;
  /** The body's look (a note field passes its own). */
  className?: string;
  /** What the caller adds to the drawn HTML (`StaticBodyDress`). */
  dress?: StaticBodyDress;
  /** A single newline is a line break (text typed into a plain box). */
  lineBreaks?: boolean;
}) {
  const html = useMemo(
    () => htmlOf(markdown, lineBreaks),
    [markdown, lineBreaks],
  );
  const box = useRef<HTMLDivElement>(null);
  return (
    <>
      <div ref={box} className={className}>
        {html === null ? (
          <pre className="tiptap whitespace-pre-wrap">{markdown}</pre>
        ) : (
          <StaticBodyHtml
            className="tiptap"
            html={html}
            markdown={markdown}
            dress={dress}
          />
        )}
        <MediaLayers />
      </div>
      {/* References get the same chips as in the editor. A sibling after the
          box, not a child: its layout effect must run once the box's ref is set. */}
      <StaticRefChips root={box} html={html} />
    </>
  );
}
